import { Button, Container, Header, Input } from '../../../../../core/components';
import { SaveOutlined } from '@ant-design/icons';
import { UtilService } from '../../../../../core/services/util.service';
import { ProfileService } from '../../profile/service/profile.service';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { User } from '../../profile/model/user.model';
import { Select, Spin } from 'antd';
interface Option {
  value: string;
  label: React.ReactNode;
  dial_code: string;
}

interface StepsProfileProps {
  onProfileUpdated?: (updatedProfile: User) => void;
}

const StepsProfile: React.FC<StepsProfileProps> = ({ onProfileUpdated }) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const countryOptions: Option[] = useMemo(() =>
    require('../../../../../assets/countries.json').map((country: any) => ({
      value: country.code,
      dial_code: country.dial_code,
      label: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img
            src={country.flag}
            alt={country.name}
            style={{
              marginRight: isMobile ? '0px' : '10px',
              width: '20px',
              height: '15px',
            }}
          />
          {!isMobile && `(${country.dial_code})`}
        </div>
      ),
    })), [isMobile]
  );
  const defaultCountry: Option = countryOptions.find((option: any) => option.value === 'CR')!;

  const [selectedCountry, setSelectedCountry] = useState<Option | null>(defaultCountry);
  const [user, setUser] = useState<User>(new User());
  const [userInitialState, setUserInitialState] = useState<User>(new User());
  const [loading, setLoading] = useState<boolean>(true);

  const loadProfile = useCallback(async () => {
    const user = await ProfileService.Get();
    setUser(user);
    setUserInitialState(user);
    setLoading(false);

    if (user.phoneNumber) {
      const possibleCountry = countryOptions.find((opt) =>
        user.phoneNumber.startsWith(opt.dial_code)
      );

      if (possibleCountry) {
        setSelectedCountry(possibleCountry);
        setUser((prev) => ({
          ...prev,
          phoneNumber: user.phoneNumber.replace(
            possibleCountry.dial_code,
            ''
          ),
        }));
      }
    }
  }, [countryOptions]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);


  const handleCountryChange = (selectedOption: Option | null) => {
    if (selectedOption) {
      setSelectedCountry(selectedOption);
      setUser((prevItem) => ({
        ...prevItem,
        phoneNumber: prevItem.phoneNumber?.replace(/^\d+/, '') || '',
      }));
    }
  };

  const OnChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'phoneNumber') {
      setUser((prevItem) => ({
        ...prevItem,
        phoneNumber: value,
      }));
    } else {
      setUser((prevItem) => ({
        ...prevItem,
        [name]: value,
      }));
    }
  };

  const Validate = (user: User) => {
    const errors: string[] = [];

    if (user.firstname.length === 0) {
      errors.push('You must enter the firtsname.');
    }

    if (user.lastname.length === 0) {
      errors.push('You must enter the lastname.');
    }

    if (user.email.length === 0) {
      errors.push('You must enter the email.');
    }

    return errors;
  };

  const Patch = async () => {
    const errors = Validate(user);

    if (errors.length > 0) {
      UtilService.Alert('Important!', 'info', errors.join(' </br> '));
      return;
    }

    const updatedUser = {
      ...user,
      phoneNumber: `${selectedCountry?.dial_code || ''}${user.phoneNumber}`,
    };

    await ProfileService.Patch(updatedUser);

    await loadProfile();
    onProfileUpdated?.(updatedUser);

    UtilService.Alert('Success!', 'success', 'The user has been updated.');
  };
  const isDataUnchanged = () => {
    const currentUser = {
      ...user,
      phoneNumber: `${selectedCountry?.dial_code || ''}${user.phoneNumber}`,
    };
    
    return JSON.stringify(currentUser) === JSON.stringify(userInitialState);
  };

  return (
    <Container>
      <Header title='My Profile' buttons={[]} />
      {loading ? (
        <div className='flex justify-center items-center p-4'>
          <Spin size='large' />
        </div>
      ) : (
        <div>
          <div className='grid grid-cols-1 lg:grid-cols-2 mb-5 gap-x-5 gap-y-5'>
            <Input
              title='First Name'
              placeholder='Enter the first name...'
              type='string'
              id='firstname'
              name='firstname'
              value={user.firstname}
              OnChange={OnChangeInput}
              validation={{
                Validator: () => user.firstname === '',
              }}
            />

            <Input
              title='Last Name'
              placeholder='Enter the last name...'
              type='string'
              id='lastname'
              name='lastname'
              value={user.lastname}
              OnChange={OnChangeInput}
              validation={{
                Validator: () => user.lastname === '',
              }}
            />
          </div>

          <div className='grid grid-cols-1 lg:grid-cols-2 mb-1 gap-x-5 gap-y-5'>
            <Input
              title='Email'
              placeholder='Enter the email...'
              type='email'
              id='email'
              name='email'
              value={user.email}
              OnChange={OnChangeInput}
              validation={{
                Validator: () => user.email === '',
              }}
            />

            <div>
              <span className='text-left block mb-2 text-sm font-medium text-gray-600'>
                Phone Number
              </span>
              <div className='flex flex-row gap-2'>
                <div>
                  <Select
                    value={selectedCountry?.value}
                    onChange={(value) => {
                      const option = countryOptions.find(
                        (opt) => opt.value === value
                      );
                      handleCountryChange(option || null);
                    }}
                    options={countryOptions}
                    style={{
                      width: '100%',
                      height: '34px',
                    }}
                  />
                </div>

                <div className='flex-1'>
                  <Input
                    placeholder='Enter the phone number...'
                    type='string'
                    id='phoneNumber'
                    name='phoneNumber'
                    value={
                      user.phoneNumber
                        ? user.phoneNumber.replace(selectedCountry?.dial_code || '', '')
                        : ''
                    }
                    OnChange={OnChangeInput}
                    validation={{
                      Validator: () => user.phoneNumber === '',
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className='flex items-center justify-end mt-6'>
            <Button
              icon={{ value: <SaveOutlined />, size: 'text-lg' }}
              text={{ value: 'Update', weight: 400, size: 'text-md' }}
              color='blue'
              tone={500}
              disabled={isDataUnchanged() || Validate(user).length !== 0}
              onClick={() => Patch()}
            />
          </div>
        </div>
      )}
    </Container>
  );
};

export default StepsProfile;
